import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FileDown, FilePlus2 } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';

import { api, unwrap } from '@/api/client';
import { mensajeDe } from '@/api/errors';
import { useConstancias, type Certificates } from '@/auth/queries';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { AVATARES } from '@/lib/avatares';

import { Cargando, ErrorCarga } from '../curso/componentes';
import { fechaDia } from '../temario/queries';

type Tipo = Certificates['types'][number]['value'];

async function abrirPdf(id: number) {
  const { data, response } = await api.GET('/api/v1/certificates/{id}/file', {
    params: { path: { id: String(id) } },
    parseAs: 'blob',
  });
  if (!response.ok || !data)
    throw new Error('No pudimos generar el documento. Inténtalo de nuevo.');
  const url = URL.createObjectURL(data);
  if (!window.open(url, '_blank', 'noopener')) {
    const a = document.createElement('a');
    a.href = url;
    a.download = 'Consalud ENARM.pdf';
    a.click();
  }
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

function hoyMx() {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Mexico_City' }).format(new Date());
}

/** Cartas y constancias (port de `inicio/constancias`). */
export function ConstanciasPage() {
  const qc = useQueryClient();
  const { data, error, isPending } = useConstancias();
  const [nuevo, setNuevo] = useState(false);
  const anioActual = Number(hoyMx().slice(0, 4));
  const [f, setF] = useState({
    type: 'constancia' as Tipo,
    gender: 'M' as 'M' | 'H',
    to: '',
    toPosition: '',
    toInstitution: '',
    sinceYear: anioActual,
    issuedOn: hoyMx(),
  });
  const crear = useMutation({
    mutationFn: () => unwrap<{ id: number }>(api.POST('/api/v1/certificates', { body: f })),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['constancias'] });
      setNuevo(false);
      toast.success('Generamos tu documento.');
      abrirPdf(r.id).catch((e: unknown) => toast.error(mensajeDe(e)));
    },
    onError: (e) => toast.error(mensajeDe(e)),
  });

  if (isPending) return <Cargando />;
  if (error) return <ErrorCarga error={error} />;

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="titulo-seccion">
          <span className="subrayado-verde">Cartas y constancias</span>
        </h1>
        {data.eligible && (
          <button type="button" className="btn-cs btn-exito" onClick={() => setNuevo(true)}>
            <FilePlus2 size={18} /> Generar nuevo documento
          </button>
        )}
      </div>
      {!data.eligible ? (
        <section className="panel-vidrio mt-16 text-center">
          <img
            src={AVATARES.doctoraSenala}
            alt=""
            className="pointer-events-none mx-auto -mt-24 h-28 w-auto sm:-mt-28 sm:h-32"
          />
          <p className="mt-3 font-bold">
            No has cursado ningún programa que permita generar cartas o constancias.
          </p>
          <Link to="/licencias" className="btn-cs btn-primario mt-5">
            Ver licencias
          </Link>
        </section>
      ) : data.items.length === 0 ? (
        <section className="panel-vidrio mt-16 text-center">
          <img
            src={AVATARES.doctor}
            alt=""
            className="pointer-events-none mx-auto -mt-24 h-28 w-auto sm:-mt-28 sm:h-32"
          />
          <p className="mt-3 text-texto-2">Aún no has generado documentos.</p>
        </section>
      ) : (
        <div className="panel-vidrio overflow-x-auto p-0">
          {/* Encabezado verde como la tabla de `inicio/constancias`. */}
          <table className="tabla-cs tabla-cs-verde">
            <thead>
              <tr>
                <th>#</th>
                <th>Documento</th>
                <th>Dirigida a</th>
                <th>Emisión</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.items.map((c, i) => (
                <tr key={c.id}>
                  <td className="tabular">{data.items.length - i}</td>
                  <td className="font-bold">{c.label}</td>
                  <td>{c.to || 'A quien corresponda'}</td>
                  <td className="whitespace-nowrap">{fechaDia(c.issuedOn, false)}</td>
                  <td className="text-right">
                    <button
                      type="button"
                      className="btn-cs btn-vidrio"
                      onClick={() =>
                        void abrirPdf(c.id).catch((e: unknown) => toast.error(mensajeDe(e)))
                      }
                    >
                      <FileDown size={16} /> PDF
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={nuevo} onOpenChange={setNuevo}>
        <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto">
          <DialogTitle>Nuevo documento</DialogTitle>
          <DialogDescription>
            Saldrá a nombre de <strong>{data.name}</strong>. Si tu nombre no es correcto, corrígelo
            en{' '}
            <Link to="/cuenta" className="underline">
              Mi cuenta
            </Link>
            .
          </DialogDescription>
          <form
            className="mt-4 grid gap-3 sm:grid-cols-2"
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              crear.mutate();
            }}
          >
            <label className="block text-sm font-bold sm:col-span-2">
              Documento
              <select
                className="input-vidrio mt-1 w-full font-normal"
                value={f.type}
                onChange={(e) => setF({ ...f, type: e.target.value as Tipo })}
              >
                {data.types.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-sm font-bold">
              Tratamiento
              <select
                className="input-vidrio mt-1 w-full font-normal"
                value={f.gender}
                onChange={(e) => setF({ ...f, gender: e.target.value as 'M' | 'H' })}
              >
                <option value="M">Doctora</option>
                <option value="H">Doctor</option>
              </select>
            </label>
            <label className="block text-sm font-bold">
              Alumno desde
              <select
                className="input-vidrio mt-1 w-full font-normal"
                value={f.sinceYear}
                onChange={(e) => setF({ ...f, sinceYear: Number(e.target.value) })}
              >
                {Array.from({ length: anioActual - 2015 }, (_, i) => anioActual - i).map((y) => (
                  <option key={y}>{y}</option>
                ))}
              </select>
            </label>
            {f.type !== 'constancia' && (
              <>
                <label className="block text-sm font-bold sm:col-span-2">
                  Dirigida a (opcional)
                  <input
                    className="input-vidrio mt-1 w-full font-normal"
                    maxLength={100}
                    placeholder="A quien corresponda"
                    value={f.to}
                    onChange={(e) => setF({ ...f, to: e.target.value })}
                  />
                </label>
                <label className="block text-sm font-bold">
                  Cargo (opcional)
                  <input
                    className="input-vidrio mt-1 w-full font-normal"
                    maxLength={100}
                    value={f.toPosition}
                    onChange={(e) => setF({ ...f, toPosition: e.target.value })}
                  />
                </label>
                <label className="block text-sm font-bold">
                  Institución (opcional)
                  <input
                    className="input-vidrio mt-1 w-full font-normal"
                    maxLength={130}
                    value={f.toInstitution}
                    onChange={(e) => setF({ ...f, toInstitution: e.target.value })}
                  />
                </label>
              </>
            )}
            <label className="block text-sm font-bold">
              Fecha de emisión
              <input
                className="input-vidrio mt-1 w-full font-normal"
                type="date"
                max={hoyMx()}
                min={`${String(f.sinceYear)}-01-01`}
                required
                value={f.issuedOn}
                onChange={(e) => setF({ ...f, issuedOn: e.target.value })}
              />
            </label>
            <div className="flex items-end justify-end sm:col-span-2">
              <button type="submit" className="btn-cs btn-exito" disabled={crear.isPending}>
                {crear.isPending ? 'Generando…' : 'Generar documento'}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
